// apps/client/src/ui-kit/BingoCard.tsx
// Interactive Bingo Card - UI Component (No dauber splat as requested)

import { motion, AnimatePresence } from 'framer-motion';

// === BINGO CELL ===
interface BingoCellProps {
    number: number | null;
    marked: boolean;
    onClick?: () => void;
}

const BingoCell: React.FC<BingoCellProps> = ({ number, marked, onClick }) => {
    const isEmpty = number === null;

    return (
        <motion.div
            onClick={isEmpty ? undefined : onClick}
            whileTap={isEmpty ? {} : { scale: 0.9 }}
            className={`
        aspect-square sm:aspect-video
        flex items-center justify-center
        rounded-lg md:rounded-xl
        font-black text-sm md:text-xl
        transition-all duration-200
        relative overflow-hidden
        select-none
        ${isEmpty
                    ? 'bg-transparent cursor-default'
                    : marked
                        ? 'bg-[#FFC107] text-[#1a0b2e] shadow-[0_0_15px_rgba(255,193,7,0.5)] scale-[0.92]'
                        : 'bg-white/5 border border-white/10 text-white hover:bg-white/10 hover:border-white/20 cursor-pointer'
                }
      `}
        >
            {number !== null && (
                <>
                    <motion.span
                        initial={false}
                        animate={{ scale: marked ? 1.2 : 1 }}
                        transition={{ type: "spring", stiffness: 400, damping: 10 }}
                        className="relative z-10"
                    >
                        {number}
                    </motion.span>

                    {/* Inner border for marked state */}
                    <AnimatePresence>
                        {marked && (
                            <motion.div
                                initial={{ scale: 1.5, opacity: 0, rotate: -10 }}
                                animate={{ scale: 0.9, opacity: 1, rotate: 0 }}
                                exit={{ scale: 1.5, opacity: 0 }}
                                transition={{ type: "spring", stiffness: 500, damping: 15 }}
                                className="absolute inset-0 border-2 border-white/60 rounded-lg md:rounded-xl shadow-[inset_0_0_15px_rgba(255,255,255,0.4)]"
                            />
                        )}
                    </AnimatePresence>
                </>
            )}
        </motion.div>
    );
};

// === BINGO CARD CONTAINER ===
// This is the VISUAL component. Logic stays in the parent.
interface BingoCardUIProps {
    grid: (number | null)[][];
    hits: number[];
    ticketId?: string | undefined;
    isPlaying?: boolean;
    showStats?: boolean;
}

export const BingoCardUI: React.FC<BingoCardUIProps> = ({
    grid,
    hits,
    ticketId,
    isPlaying = false,
    showStats = true,
}) => {
    const hitSet = new Set(hits);

    // Count completed lines
    let completedLines = 0;
    for (const row of grid) {
        const numbersInRow = row.filter((n): n is number => n !== null);
        const allHit = numbersInRow.every((n) => hitSet.has(n));
        if (allHit && numbersInRow.length > 0) {
            completedLines++;
        }
    }

    const totalNumbers = grid.flat().filter(n => n !== null).length;
    const lineStatus = completedLines >= 3 ? '🎉 ¡BINGO!' : completedLines === 2 ? '🎊 ¡2 Líneas!' : completedLines === 1 ? '🎯 ¡1 Línea!' : '';

    return (
        <div className="bg-white/5 border border-white/10 backdrop-blur-xl rounded-[2rem] p-4 md:p-6 shadow-2xl">
            {/* Header */}
            {ticketId && (
                <div className="mb-4 flex justify-between items-center px-2">
                    <div>
                        <h3 className="text-white font-black italic uppercase tracking-tighter text-sm">
                            Cosmic Ticket {ticketId}
                        </h3>
                        <p className="text-[10px] text-gray-500 font-bold uppercase tracking-widest">
                            {isPlaying ? '🔴 EN VIVO' : 'Esperando...'}
                        </p>
                    </div>
                    <div className="flex gap-1">
                        {[1, 2, 3].map(i => (
                            <div key={i} className={`w-2 h-2 rounded-full ${i <= completedLines ? 'bg-[#FFC107]' : 'bg-[#FFC107]/20'}`} />
                        ))}
                    </div>
                </div>
            )}

            {/* Grid Container with Golden Border */}
            <div className="bg-[#100b2b]/50 p-2 md:p-3 rounded-2xl border-2 border-[#E88A00]/50">
                <div className="grid grid-cols-9 gap-1 md:gap-2">
                    {grid.flat().map((cell, idx) => {
                        const isMarked = cell !== null && hitSet.has(cell);
                        return (
                            <BingoCell
                                key={idx}
                                number={cell}
                                marked={isMarked}
                            />
                        );
                    })}
                </div>
            </div>

            {/* Stats Footer */}
            {showStats && (
                <div className="mt-4 flex justify-between text-sm">
                    <span className="text-gray-400">
                        ✅ {hits.length} / {totalNumbers} marcados
                    </span>
                    <span className="text-[#FFC107] font-bold">
                        {lineStatus}
                    </span>
                </div>
            )}
        </div>
    );
};

// Re-export for backwards compatibility with old showcase
export { BingoCardUI as BingoCard };
